import { useCallback, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useAdminCriticalFlow } from '../../app/pwa/adminCriticalFlowGate.js';
import { confirmApi } from './confirm/confirmApi.js';
import {
  acquireDirtyLeavePatch,
  releaseDirtyLeavePatch,
  resetDirtyLeavePatchForTests,
} from './dirtyLeaveHistoryPatch.js';

export interface DirtyStateGuardOptions {
  readonly title?: string;
  readonly message?: string;
}

export interface DirtyStateGuardProps extends DirtyStateGuardOptions {
  /** When true, warn on leave (browser unload + SPA history) and mark critical flow. */
  readonly when: boolean;
}

export { resetDirtyLeavePatchForTests };

/**
 * Spec §12.8 leave-warn helper.
 * - `beforeunload` for tab close / refresh
 * - sync confirm for SPA history push/replace (BrowserRouter has no data-router blocker)
 * - `confirmLeave()` for programmatic cancel/close via ConfirmDialog
 * - `useAdminCriticalFlow` so PWA update deferral respects dirty forms
 */
export function useDirtyStateGuard(
  when: boolean,
  options: DirtyStateGuardOptions = {},
): {
  confirmLeave: () => Promise<boolean>;
} {
  const { t } = useTranslation('admin');
  const title =
    options.title ??
    t('shared.dirtyLeave.title', { defaultValue: 'Unsaved changes' });
  const message =
    options.message ??
    t('shared.dirtyLeave.message', {
      defaultValue: 'You have unsaved changes. Leave without saving?',
    });

  useAdminCriticalFlow(when);

  useEffect(() => {
    if (!when) {
      return undefined;
    }
    acquireDirtyLeavePatch(title, message);
    return (): void => {
      releaseDirtyLeavePatch();
    };
  }, [when, title, message]);

  const confirmLeave = useCallback(async (): Promise<boolean> => {
    if (!when) {
      return true;
    }
    return confirmApi({
      title,
      message,
      variant: 'warning',
    });
  }, [when, title, message]);

  return { confirmLeave };
}
